import { useCallback, useState } from 'react';
import styled from 'styled-components';

import { focusOutline } from '@hivebrite/design-system/Theme';
import { Tooltip, POSITIONS } from '@hivebrite/design-system/Tooltip';
import { useIsMobileDevice } from '@hivebrite/react-hooks';

import onEnterOrSpacePress from 'hb-react/shared/utils/onEnterOrSpacePress';

import useHeaderTheme from '../hooks/useHeaderTheme';

interface Props {
  activeIcon: React.ReactNode;
  children?: React.ReactNode;
  defaultIcon: React.ReactNode;
  fixedParent?: boolean;
  id?: string;
  onClick?: () => void;
  onMouseEnter?: () => void;
  role?: string;
  tooltip: React.ReactNode;
}

const HeaderDefaultLink = ({
  children,
  activeIcon,
  defaultIcon,
  onClick,
  onMouseEnter,
  tooltip,
  fixedParent,
  ...props
}: Props) => {
  const headerTheme = useHeaderTheme();

  const isMobileDevice = useIsMobileDevice();
  const [isActiveIcon, setIsActiveIcon] = useState(false);
  const [isTooltipDisplayed, setIsTooltipDisplayed] = useState(false);

  const handleFocus = useCallback(() => {
    if (!isMobileDevice) {
      setIsActiveIcon(true);
      setIsTooltipDisplayed(true);
    }
  }, [isMobileDevice]);

  const handleBlur = useCallback(() => {
    setIsActiveIcon(false);
    setIsTooltipDisplayed(false);
  }, []);

  const handleMouseEnter = useCallback(() => {
    onMouseEnter?.();
    if (!isMobileDevice) {
      setIsActiveIcon(true);
    }
  }, [isMobileDevice]);

  const handleMouseLeave = useCallback(() => {
    setIsActiveIcon(false);
    setIsTooltipDisplayed(false);
  }, []);

  return (
    <Wrapper
      onMouseDown={handleBlur}
      onMouseUp={onClick}
      onKeyDown={onEnterOrSpacePress(onClick)}
      role="button"
      tabIndex={0}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      onFocus={handleFocus}
      onBlur={handleBlur}
      $headerColor={headerTheme.color}
      {...props}
    >
      <Tooltip
        initialPosition={POSITIONS.BOTTOM}
        childrenElement={tooltip}
        hideOnScreenReader
        shouldRenderTooltip={isTooltipDisplayed}
        fixedParent={fixedParent}
      >
        {isActiveIcon ? activeIcon : defaultIcon}
        {children}
      </Tooltip>
    </Wrapper>
  );
};

export default HeaderDefaultLink;

const Wrapper = styled.div<{ $headerColor: string }>`
  display: inline-block;
  padding: 0.8em 0.98em;
  cursor: pointer;
  ${focusOutline}
`;
